import Button from "@app/components/button";
import Checkbox from "@app/components/checkbox";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import TextArea from "@app/components/textarea";
import { Images } from "@assets/images";
import { Form, Formik } from "formik";
import CustomAsyncSelect from "@app/components/custom-async-select";
import { BookOptionSelect } from "@app/components/book-option-select";
import { CustomSelect } from "@app/components/select/custom-select";
import {
  BUCKET_URL_KEY,
  DATE_TIME_FORMAT_2,
  MAX_SIZE_UPLOAD,
  REGEX_URL,
  RoutePath,
  SystemMessage,
} from "@app/constants";
import CategoryService from "@app/services/http/category.service";
import {
  BackgroundMusic,
  BookEbookFile,
  BookElement,
  BookElementChildren,
  LabelValue,
  ModelTrain,
  UpdateBook,
  UpdateBookForm,
} from "@app/types";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import BackgroundMusicService from "@app/services/http/background-music.service";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { convertDate, formatDate, formatDateTime } from "@app/utils/util";
import { createBookValidationSchema } from "@app/validations";
import { useNavigate, useParams } from "react-router-dom";
import Chapter from "../components/chapter.component";
import BookService from "@app/services/http/book.service";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import VoiceConvertService from "@app/services/http/voice-convert.service";
import Audiobook from "@app/components/audiobook";
import {
  clearAudiobook,
  sendAudiobook,
  showAudiobookPanel,
} from "@app/store/audiobook";
import { useDispatch, useSelector } from "react-redux";
import CustomAsyncMultiSelect from "@app/components/custom-async-multi-select";
import TryListeningCheckbox from "../components/try-listening-checkbox.component";
import { PromiseOptionsCallback } from "@app/components/custom-async-select/custom-async-select.component";
import UploadMediaService from "@app/services/http/upload-media.service";
import { addToast } from "@app/components/toast/toast.service";
import StorageService from "@core/services/storage";
import { ScrollToError } from "@core/components/scroll-to-error/scroll-to-error.component";
import clsx from "clsx";
import { selectAudiobook } from "@app/store/audiobook";
import { EndpointConversion } from "@app/types/voice-convert.type";
import AuthorService from "@app/services/http/author.service";
import { selectAuth } from "@app/store/auth";
import { openPortalDialog } from "@app/services/modal.service";
import ConfirmModal from "@app/components/confirm-modal";
import AiThumbnailModal from "@app/components/ai-thumbnail-modal";
import EbookPreviewModal from "@app/components/ebook-preview-modal";
import { ThumbnailByAIPayload } from "@app/types";
import "./update-book.style.css";

function UpdateBookPage() {
  const bookMediaTypeOptions = [
    { label: "Audio", value: "1" },
    { label: "Ebook", value: "2" },
    { label: "Audio + Ebook", value: "3" },
  ];
  const inputThumbnailRef = useRef<HTMLInputElement>(null);
  const previewThumbnailRef = useRef<HTMLImageElement>(null);
  const inputAdditionalThumbnailRef = useRef<HTMLInputElement>(null);
  const s3Url = StorageService.getObject(BUCKET_URL_KEY);

  const { abortController } = useCancellationRequest();

  const [, forceUpdate] = useForceUpdate();

  const [isLoading, setIsLoading] = useState(false);
  const [isComingSoon, setIsComingSoon] = useState(false);
  const [isGenerateThumbnailsByAI, setIsGenerateThumbnailsByAI] =
    useState(false);
  const [selectedMediaType, setSelectedMediaType] = useState("1");
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [ebookUploadFile, setEbookUploadFile] = useState<File | null>(null);
  const [ebookFileInfo, setEbookFileInfo] = useState<BookEbookFile | null>(null);
  const [isPreviewingEbook, setIsPreviewingEbook] = useState(false);
  const [currentThumbnails, setCurrentThumbnails] = useState<string[]>([]);
  const [additionalThumbnails, setAdditionalThumbnails] = useState<string[]>(
    []
  );
  const [bgAudioSelectedId, setBgAudioSelectedId] = useState(0);
  const [bgAudios, setBgAudios] = useState<BackgroundMusic[]>([]);
  const [errorMessage, setErrorMessage] = useState<{
    thumbnail: string;
    additionalThumbnail?: string;
    ebookFile?: string;
  }>({
    thumbnail: "",
    additionalThumbnail: "",
    ebookFile: "",
  });
  const [modelTrains, setModelTrains] = useState<ModelTrain[]>([]);
  const [endpointConversions, setEndpointConversions] = useState<
    EndpointConversion[]
  >([]);
  const [audioProcessDate, setAudioProcessDate] = useState<number | null>(null);

  const [initialValues, setInitialValues] = useState<UpdateBookForm>({
    name: "",
    categoryIds: [],
    bookOption: "",
    price: "",
    ebookPrice: "0",
    mediaType: "1",
    oldPrice: "",
    linkStore: "",
    timePublish: "",
    description: "",
    isPopular: false,
    isDraft: false,
    isTryListening: false,
    elements: [
      {
        description: "",
        name: "",
        point: "",
        isForceUpdateAudio: false,
        isAutoGenerateThumbnail: false,
        filenames: "",
        thumbnails: [],
        audios: [],
        modelConverts: [],
        children: [],
      },
    ],
    authorIds: [],
    musicId: null,
    isGenerateThumbnailsByAI: false,
    authorName: "",
    genreName: "",
  });

  const { bookId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isPanelShow } = useSelector(selectAudiobook);
  const userInfo = useSelector(selectAuth);
  const hasStoredEbookFile = !!ebookFileInfo?.id;

  const loadBookData = useCallback(() => {
    if (!bookId || isNaN(Number(bookId))) {
      return;
    }

    // Reset audioProcessDate when reloading
    setAudioProcessDate(null);
    setIsLoading(true);

    Promise.all([
      BookService.get(Number(bookId), undefined, false),
      BookService.getEbookFile(Number(bookId), false).catch(() => null),
      VoiceConvertService.getEndpointConversions(abortController.signal, false),
      VoiceConvertService.getModelTrains(abortController.signal, false),
    ]).then(([bookRes, ebookFileRes, endpointConversionsRes, modelTrainsRes]) => {
      const data = bookRes.data;

      // Set audioProcessDate for audio processing notification
      setAudioProcessDate(data.audioProcessDate || null);

      const newModelTrains = modelTrainsRes.data.items;

      setModelTrains(newModelTrains);
      setEndpointConversions(endpointConversionsRes.data.items);
      const latestEbookFile =
        ebookFileRes?.data?.id
          ? ebookFileRes.data
          : data.ebookFile?.id
            ? data.ebookFile
            : null;

      setEbookFileInfo(latestEbookFile);
      setSelectedMediaType(String(data.mediaType ?? 1));

      const parseChildrenRecursively = (
        childrenData: any[]
      ): BookElementChildren[] => {
        return childrenData.map((childData) => {
          const item: BookElementChildren = {
            id: childData.id,
            name: childData.name,
            point: childData.point,
            description: childData.description ?? "",
            content: childData.content,
            isForceUpdateAudio: childData.isForceUpdateAudio,
            isAutoGenerateThumbnail: childData.isAutoGenerateThumbnail,
            thumbnails: (childData.thumbnails?.map((item: any) => item) ??
              []) as any,
            audios:
              childData?.audios?.map((item: any) => ({
                name: item.name,
                audio: item.audio,
              })) ?? [],
            modelConverts:
              childData?.modelConverts?.map((item: any) => {
                const model = newModelTrains.find(
                  (model) => model.value === (item.model as any)?.value
                );

                return {
                  endpointConvertId: item.endpointConvertId,
                  modelConvertId: item.modelConvertId,
                  endpoint: item.endpoint,
                  model: model,
                };
              }) ?? [],
            children:
              childData.child && childData.child.length > 0
                ? parseChildrenRecursively(childData.child)
                : [],
          };

          if (childData.endpointConvertId) {
            item.endpointConvertId = childData.endpointConvertId;
          }

          if (childData.modelConvertId) {
            item.modelConvertId = childData.modelConvertId;
          }

          return item;
        });
      };

      const elements: BookElement[] = [];

      for (const element of data.elements) {
        const elementChildrens: BookElementChildren[] =
          element.child && element.child.length > 0
            ? parseChildrenRecursively(element.child)
            : [];

        const el: BookElement = {
          id: element.id,
          name: element.name,
          point: element.point?.toString() ?? 0,
          description: element.description ?? "",
          content: element.content,
          children: elementChildrens,
          isForceUpdateAudio: element.isForceUpdateAudio,
          isAutoGenerateThumbnail: element.isAutoGenerateThumbnail,
          thumbnails: (element.thumbnails?.map((item) => item) ?? []) as any,
          audios:
            element?.audios?.map((item) => ({
              name: item.name ?? "",
              audio: item.audio,
            })) ?? [],
          modelConverts:
            element?.modelConverts?.map((item) => {
              const model = newModelTrains.find(
                (model) => model.value === (item.model as any)?.value
              );

              return {
                endpointConvertId: item.endpointConvertId,
                modelConvertId: item.modelConvertId,
                endpoint: item.endpoint,
                model: model,
              };
            }) ?? [],
        };

        if (element.endpointConvertId) {
          el.endpointConvertId = element.endpointConvertId;
        }

        if (element.modelConvertId) {
          el.modelConvertId = element.modelConvertId;
        }

        elements.push(el);
      }

      setCurrentThumbnails(data.thumbnails?.map((item) => item) || []);

      if (data?.backgroundMusic?.id) {
        setBgAudioSelectedId(data.backgroundMusic.id);
      }

      setIsComingSoon(data.timePublish > 0);
      setInitialValues({
        name: data.name,
        categoryIds: data.categories.map((item) => ({
          label: item.name,
          value: item.id,
        })),
        bookOption: data.bookOption.toString(),
        mediaType: String(data.mediaType ?? 1),
        linkStore: data.linkStore,
        description: data.description,
        isPopular: data.isPopular,
        isDraft: data.isDraft,
        timePublish: data.timePublish
          ? formatDate(data.timePublish * 1000, DATE_TIME_FORMAT_2)
          : null,
        isTryListening: data.isTryListening,
        price: data.price.toString(),
        ebookPrice: String(data.ebookPrice ?? 0),
        oldPrice: data.oldPrice.toString(),
        elements,
        authorIds: data.authors.map((item) => ({
          label: item.fullName,
          value: item.id,
        })),
        musicId: {
          label: data.backgroundMusic.name,
          value: data.backgroundMusic.id,
        },
        isGenerateThumbnailsByAI: Boolean(data.isGenerateThumbnailsByAI),
        authorName: userInfo.fullName ?? "",
        genreName: data.genreName || "",
      } as any);
    })
      .catch(() => {
        // Handle error silently or show toast if needed
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [bookId, abortController.signal, userInfo.fullName]);

  useEffect(() => {
    if (isNaN(Number(bookId))) {
      return navigate(`/${RoutePath.BOOK_MANAGEMENT}`);
    }

    loadBookData();
  }, [bookId, navigate, loadBookData]);

  useEffect(() => {
    return () => {
      dispatch(clearAudiobook());
    };
  }, []);

  useEffect(() => {
    if (isLoading) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isLoading]);

  const bgAudioSelectedSrc = useMemo(() => {
    const selected = bgAudios.find((item) => item.id === bgAudioSelectedId);

    return selected ? selected.link : "";
  }, [bgAudioSelectedId, bgAudios]);

  const getCategoriesOptions = (inputValue: string) => {
    return new Promise<LabelValue[]>((resolve) => {
      CategoryService.getList(
        { keyword: inputValue.trim() },
        abortController.signal,
        false
      ).then((res) => {
        const data: LabelValue[] = res.data.items.map((item) => ({
          label: item.name,
          value: item.id,
        }));

        resolve(data);
      });
    });
  };

  const getMusicsOptions = useCallback(
    (inputValue: string, callback: PromiseOptionsCallback) => {
      BackgroundMusicService.getList(
        { keyword: inputValue.trim() },
        abortController.signal,
        false
      ).then((res) => {
        const data: LabelValue[] = res.data.items.map((item) => ({
          label: item.name,
          value: item.id,
        }));

        setBgAudios(res.data.items);

        callback(data);
      });
    },
    []
  );

  const onAddThumbnailFileClick = () => {
    if (inputThumbnailRef.current) {
      inputThumbnailRef.current.click();
    }
  };

  const onAddAdditionalThumbnailFilesClick = () => {
    if (inputAdditionalThumbnailRef.current) {
      inputAdditionalThumbnailRef.current.click();
    }
  };

  const handleThumbnailFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) {
      if (previewThumbnailRef.current) {
        previewThumbnailRef.current.src = "";
      }

      setThumbnail(null);

      const newErrorMessage = {
        ...errorMessage,
        thumbnail: "",
      };

      setErrorMessage(newErrorMessage);

      return;
    }

    const file = event.target.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      if (previewThumbnailRef.current) {
        previewThumbnailRef.current.src = "";
      }

      setThumbnail(null);

      const newErrorMessage = {
        ...errorMessage,
        thumbnail: SystemMessage.MAX_SIZE_UPLOAD,
      };

      setErrorMessage(newErrorMessage);

      return;
    }

    // Upload the file immediately
    const formData = new FormData();

    formData.append("medias", file);

    UploadMediaService.upload({ medias: [file] }, false)
      .then((response) => {
        // Update the preview with the uploaded image URL
        if (previewThumbnailRef.current && response.data) {
          const uploadedImageUrl = s3Url + response.data.items[0].url;

          previewThumbnailRef.current.src = uploadedImageUrl;
        }

        // Store the URL string instead of the file
        if (response.data && response.data.items[0]) {
          setThumbnail(response.data.items[0].url);
        }

        const newErrorMessage = {
          ...errorMessage,
          thumbnail: "",
        };

        setErrorMessage(newErrorMessage);
      })
      .catch(() => {
        setThumbnail(null);

        const newErrorMessage = {
          ...errorMessage,
          thumbnail: "",
        };

        setErrorMessage(newErrorMessage);
      });
  };

  const handleAdditionalThumbnailFilesChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;

    if (!files?.length) {
      setAdditionalThumbnails([]);

      return;
    }

    let isValid = true;
    const newFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      newFiles.push(file);

      if (file.size > MAX_SIZE_UPLOAD) {
        isValid = false;
      }
    }

    if (!isValid) {
      const newErrorMessage = {
        ...errorMessage,
        additionalThumbnail: SystemMessage.MAX_SIZE_UPLOAD,
      };

      setErrorMessage(newErrorMessage);

      return;
    }

    try {
      // Upload files to server
      const response = await UploadMediaService.upload({
        medias: newFiles,
      }, false);

      if (response.data?.items) {
        // Extract URLs from the response
        let uploadedUrls: string[] = [];

        if (response.data.items && response.data.items.length > 0) {
          // If we have multiple items
          uploadedUrls = response.data.items.map((item) => item.url);
        }

        // Set the uploaded URLs to state
        setAdditionalThumbnails(uploadedUrls);
      }
    } catch (error) {
      const newErrorMessage = {
        ...errorMessage,
        additionalThumbnail: "Failed to upload files. Please try again.",
      };

      setErrorMessage(newErrorMessage);
    }
  };

  const supportsEbook = (mediaType?: string | number) =>
    String(mediaType) === "2" || String(mediaType) === "3";

  const handleMediaTypeChange = (value: string | number) => {
    const nextValue = String(value);

    setSelectedMediaType(nextValue);

    if (!supportsEbook(nextValue)) {
      setEbookUploadFile(null);
      setEbookFileInfo(null);
      setErrorMessage((prev) => ({ ...prev, ebookFile: "" }));
    }
  };

  const handleEbookUploadFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      setEbookUploadFile(null);

      return;
    }

    if (file.type !== "application/pdf") {
      setErrorMessage((prev) => ({
        ...prev,
        ebookFile: "Chỉ hỗ trợ file PDF",
      }));
      setEbookUploadFile(null);

      return;
    }

    setEbookUploadFile(file);
    setErrorMessage((prev) => ({ ...prev, ebookFile: "" }));
  };

  const openLocalEbookPreview = (file: File) => {
    const previewUrl = URL.createObjectURL(file);
    const modalRef = openPortalDialog(EbookPreviewModal, {
      title: file.name,
      previewUrl,
    });

    modalRef.afterClosed().subscribe(() => {
      URL.revokeObjectURL(previewUrl);
    });
  };

  const handlePreviewEbookFile = async () => {
    if (ebookUploadFile) {
      openLocalEbookPreview(ebookUploadFile);

      return;
    }

    if (!bookId || !ebookFileInfo) {
      return;
    }

    try {
      setIsPreviewingEbook(true);
      const blob = await BookService.previewEbookFile(Number(bookId));
      const previewUrl = URL.createObjectURL(blob);
      const modalRef = openPortalDialog(EbookPreviewModal, {
        title: `Preview Ebook #${bookId}`,
        previewUrl,
      });

      modalRef.afterClosed().subscribe(() => {
        URL.revokeObjectURL(previewUrl);
      });
    } catch (error) {
      addToast({ text: "Không thể preview ebook", status: "inValid" });
    } finally {
      setIsPreviewingEbook(false);
    }
  };

  const handleComingSoonChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsComingSoon(e.target.checked);
  };

  const handleGenerateThumbnailsByAIChange = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = e.target.checked;

    setIsGenerateThumbnailsByAI(isChecked);

    if (isChecked && bookId) {
      // Open AI thumbnail modal directly for update
      const modalRef = openPortalDialog(AiThumbnailModal, {
        bookName: initialValues.name || "",
        authorName: initialValues.authorName || userInfo.fullName || "",
        onSubmit: async (aiData: ThumbnailByAIPayload) => {
          try {
            await BookService.thumbnailByAI(Number(bookId), aiData, false);
            addToast({ text: "Đang tạo bìa AI, vui lòng chờ..." });
            // Refresh the page data
            forceUpdate();
          } catch (error) {
            addToast({ text: "Có lỗi khi tạo bìa AI", status: "inValid" });
          }
        },
      });

      modalRef.afterClosed().subscribe(() => {
        setIsGenerateThumbnailsByAI(false);
      });
    }
  };

  const handleRemoveCurrentThumbnail = (indexToRemove: number) => {
    setCurrentThumbnails((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleRemoveAdditionalThumbnail = (indexToRemove: number) => {
    setAdditionalThumbnails((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleRemoveMainThumbnail = () => {
    setThumbnail(null);

    if (previewThumbnailRef.current) {
      previewThumbnailRef.current.src = "";
    }

    // Clear any error messages
    const newErrorMessage = {
      ...errorMessage,
      thumbnail: "",
    };

    setErrorMessage(newErrorMessage);
  };

  const handleDeleteEbookFile = () => {
    if (!bookId) {
      return;
    }

    BookService.deleteEbookFile(Number(bookId), false).then(() => {
      setEbookFileInfo(null);
      setEbookUploadFile(null);
      addToast({ text: "Đã xóa file ebook" });
    });
  };

  const handleReprocessEbookFile = () => {
    if (!bookId) {
      return;
    }

    BookService.reprocessEbookFile(Number(bookId), false).then((response) => {
      setEbookFileInfo(response.data);
      addToast({ text: "Đã gửi yêu cầu xử lý lại PDF ebook" });
    });
  };

  const handleSubmit = (values: UpdateBookForm) => {
    const confirmModalObs = openPortalDialog(ConfirmModal, {
      message: "Bạn có chắc chắn muốn cập nhật sách này không?",
    });

    confirmModalObs.afterClosed().subscribe((data) => {
      if (data?.isAccept) {
        performUpdate(values);
      }
    });
  };

  const performUpdate = (values: UpdateBookForm) => {
    setIsLoading(true);
    
    let finalThumbnails: string[] = [];
    const hasNewMain = !!thumbnail;
    const hasNewAdditional = additionalThumbnails.length > 0;

    if (hasNewMain || hasNewAdditional) {
      if (hasNewMain) {
        finalThumbnails.push(thumbnail);
      } else if (currentThumbnails.length > 0) {
        finalThumbnails.push(currentThumbnails[0]);
      }

      if (hasNewAdditional) {
        finalThumbnails.push(...additionalThumbnails);
      } else if (currentThumbnails.length > 1) {
        finalThumbnails.push(...currentThumbnails.slice(1));
      }
    } else {
      finalThumbnails = [...currentThumbnails];
    }

    const updateBook: UpdateBook = {
      name: values.name.trim(),
      price: Number(values.price),
      ebookPrice: Number(values.ebookPrice) || 0,
      mediaType: Number(values.mediaType) || 1,
      oldPrice: Number(values.oldPrice),
      description: values.description.trim(),
      categoryIds: (values.categoryIds as any).map(
        (item: LabelValue) => item?.value
      ),
      authorIds: values.authorIds?.map((item: LabelValue) => +item?.value),
      bookOption: values.bookOption,
      musicId: values.musicId?.value ? Number(values.musicId?.value) : 0,
      linkStore: values.linkStore,
      isPopular: values.isPopular,
      isTryListening: !!values.isTryListening,
      isDraft: values.isDraft,
      timePublish: isComingSoon
        ? convertDate(values.timePublish, DATE_TIME_FORMAT_2).getTime() / 1000
        : 0,
      thumbnails: finalThumbnails,
      elements: values.elements,
      isGenerateThumbnailsByAI: values.isGenerateThumbnailsByAI,
      authorName: values.authorName.trim(),
      genreName: values.genreName.trim() || "",
    };

    const transformChildrenRecursively = (
      children: BookElementChildren[]
    ): BookElementChildren[] => {
      return children.map((child) => {
        if (child.modelConverts && child.modelConverts.length > 0) {
          child.modelConverts = child.modelConverts.map((convert) => ({
            endpointConvertId: Number(convert.endpoint?.value) || 0,
            modelConvertId: Number(convert.model?.value) || 0,
          }));
        }

        if (child.children && child.children.length > 0) {
          child.children = transformChildrenRecursively(child.children);
        }

        delete child.filenames;
        delete child.newThumbnails;

        return child;
      });
    };

    if (updateBook.elements && updateBook.elements.length > 0) {
      updateBook.elements = updateBook.elements.map((element) => {
        if (element.modelConverts && element.modelConverts.length > 0) {
          element.modelConverts = element.modelConverts.map((convert) => ({
            endpointConvertId: Number(convert.endpoint?.value) || 0,
            modelConvertId: Number(convert.model?.value) || 0,
          }));
        }

        if (element.children && element.children.length > 0) {
          element.children = transformChildrenRecursively(element.children);
        }

        delete element.filenames;
        delete element.newThumbnails;

        return element;
      });
    }

    BookService.update(Number(bookId), updateBook, false)
      .then(async () => {
        if (supportsEbook(values.mediaType) && ebookUploadFile) {
          const uploadResponse = await BookService.uploadEbookFile(
            Number(bookId),
            ebookUploadFile,
            false
          );

          setEbookFileInfo(uploadResponse.data);
          setEbookUploadFile(null);
        }

        const newErrorMessage = {
          ...errorMessage,
          thumbnail: "",
        };

        setErrorMessage(newErrorMessage);
        addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
        
        // Reload book data after update to get latest information
        loadBookData();
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const handleCancelClick = () => {
    navigate(`/${RoutePath.BOOK_MANAGEMENT}`);
  };

  const handleClickPreviewAudio = (chapterName: string, linkAudio?: string) => {
    if (linkAudio) {
      dispatch(showAudiobookPanel());
      dispatch(sendAudiobook({ chapterName, linkAudio }));
    }
  };

  const getAuthorsOptions = useCallback(
    (inputValue: string) =>
      new Promise<LabelValue[]>((resolve) => {
        AuthorService.getList(abortController.signal, false, {
          keyword: inputValue,
          sortType: "ASC",
        }).then((res) => {
          const data: LabelValue[] = res.data.items;

          resolve(data);
        });
      }),
    [abortController.signal]
  );

  return (
    <div className={clsx("py-6 px-12", { "pb-32": isPanelShow })}>
      {/* Loading Overlay */}
      {isLoading && (
        <div 
          className="fixed top-0 left-0 right-0 bottom-0 z-[9999] flex justify-center items-center"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
        >
          <span className="loader"></span>
        </div>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={createBookValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnBlur
        validateOnChange
      >
        <Form>
          <div className="fixed top-[120px] right-12 z-50">
            <Button
              type="submit"
              label="Lưu"
              width={164}
              containerClassName="mr-2"
              className="h-[52px] rounded-lg border border-solid border-[#5570F1] bg-white font-semibold text-[#5570F1] shadow-lg"
              theme="primary"
            />
          </div>
          <ScrollToError />
          <div className="flex justify-between items-center ">
            <div className="text-[#343C6A]">Danh sách sách / Sửa</div>
          </div>

          {/* Audio Processing Alert */}
          {audioProcessDate && (
            <div className="mt-6 p-4 bg-[#FFF4E6] border border-[#FFB84D] rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-[#FF8C00] rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
                <div className="text-[#FF8C00] font-medium">
                  Sách đang trong quá trình tạo audio tự động, thời gian dự kiến
                  hoàn thành{" "}
                  <span className="font-semibold">
                    {formatDateTime(audioProcessDate)}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-6">
            <div className="mt-6 rounded-xl bg-white p-7 flex-[6]">
              <FormControl name="name">
                <Input placeholder="Tên sách, tác phẩm" />
              </FormControl>
              <div className="flex mt-6 gap-3">
                <div className="flex-1">
                  <FormControl name="categoryIds">
                    <CustomAsyncMultiSelect
                      promiseOptions={getCategoriesOptions}
                      placeholder="Danh mục"
                      className="bg-color !h-[52px] rounded-lg"
                    />
                  </FormControl>
                </div>
                <div className="flex-1">
                  <FormControl name="bookOption">
                    <BookOptionSelect />
                  </FormControl>
                </div>
              </div>
              <div className="flex mt-6 gap-3">
                <div className="flex-1">
                  <FormControl name="oldPrice">
                    <Input placeholder="Giá bìa" />
                  </FormControl>
                </div>
                <div className="flex-1">
                  <FormControl name="price">
                    <Input placeholder="Giá bán khuyến mãi" />
                  </FormControl>
                </div>
              </div>
              <div className="flex mt-6 gap-3">
                <div className="flex-1">
                  <FormControl name="mediaType">
                    <CustomSelect
                      options={bookMediaTypeOptions}
                      value={selectedMediaType}
                      className="bg-color !h-[52px] rounded-lg px-2"
                      optionContainerClassName="overscroll-contain mt-1 rounded-b-lg overflow-y-auto"
                      optionClassName="whitespace-nowrap px-3 py-3 border-b border-solid w-auto"
                      labelSelectedClassName="black-50 !mx-3"
                      placeholder="Loại sách"
                      onChange={handleMediaTypeChange}
                    />
                  </FormControl>
                  <div className="mt-2 text-sm text-[#FF4D4F]">
                    Bạn có thể chọn loại sách để thao tác (Audio/Ebook)
                  </div>
                </div>
                {supportsEbook(selectedMediaType) && (
                  <div className="flex-1">
                    <FormControl name="ebookPrice">
                      <Input placeholder="Giá ebook" />
                    </FormControl>
                  </div>
                )}
              </div>
              <div className="flex mt-6 gap-[15px]">
                <div className="flex-1">
                  <FormControl name="authorIds">
                    <CustomAsyncMultiSelect
                      promiseOptions={getAuthorsOptions}
                      placeholder="Tác giả"
                    />
                  </FormControl>
                </div>
                <div className="flex-1 flex gap-1 items-center">
                  <div className="flex-1">
                    <FormControl name="musicId">
                      <CustomAsyncSelect
                        promiseOptions={getMusicsOptions}
                        placeholder="Nhạc nền bấm  xổ ra và nghe thử ở từng dòng"
                        onChange={(option) => {
                          if (option) {
                            setBgAudioSelectedId(option.value as number);
                          }
                        }}
                      />
                    </FormControl>
                  </div>
                  <div className="flex items-center">
                    {bgAudioSelectedSrc?.match(REGEX_URL) && (
                      <div>
                        <abbr title="Nghe audio">
                          <img
                            className="w-8 cursor-pointer"
                            src={Images.PlayPreviewIcon.default}
                            alt=""
                            onClick={() =>
                              handleClickPreviewAudio(
                                "nghe nhạc nền",
                                bgAudioSelectedSrc
                              )
                            }
                          />
                        </abbr>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <FormControl name="linkStore">
                <Input className="mt-6" placeholder="Link mua sách giấy" />
              </FormControl>
              <FormControl name="description">
                <TextArea
                  textAreaClassName="bg-color border-none outline-none rounded-lg !px-4 !py-3"
                  className="mt-6"
                  width="100%"
                  height="auto"
                  rows={7}
                  placeholder="Nội dung giới thiệu sách, tác phẩm"
                  isStandardizeContent={true}
                />
              </FormControl>
              {supportsEbook(selectedMediaType) && (
                <div className="mt-6 rounded-xl border border-dashed border-[#CFD3D4] p-4">
                  <div className="text-sm font-semibold text-[#343C6A]">
                    Quản lý PDF Ebook
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleEbookUploadFileChange}
                    />
                    {!!ebookUploadFile?.name && (
                      <div className="text-[#38499B]">{ebookUploadFile.name}</div>
                    )}
                    {hasStoredEbookFile && (
                      <Button
                        label={isPreviewingEbook ? "Đang tải..." : "Preview Ebook"}
                        width=""
                        className="rounded-xl px-5"
                        disabled={isPreviewingEbook}
                        onClick={handlePreviewEbookFile}
                      />
                    )}
                  </div>
                  {!!errorMessage.ebookFile && (
                    <div className="mt-2 text-xs text-[#FF4D4F]">
                      {errorMessage.ebookFile}
                    </div>
                  )}
                  <div className="mt-4 rounded-lg bg-[#F6F8FB] p-4 text-sm text-[#3A4251]">
                    <div>Trạng thái: {ebookFileInfo?.status ?? "Chưa có file"}</div>
                    <div>Trang: {ebookFileInfo?.pageCount ?? 0}</div>
                    {!!ebookFileInfo?.errorMessage && (
                      <div className="text-[#FF4D4F]">
                        Lỗi: {ebookFileInfo.errorMessage}
                      </div>
                    )}
                  </div>
                  {hasStoredEbookFile && (
                    <div className="mt-4 flex gap-3">
                      <Button
                        label="Xử lý lại PDF"
                        width=""
                        className="rounded-xl px-5"
                        onClick={handleReprocessEbookFile}
                      />
                      <Button
                        label="Xóa file ebook"
                        width=""
                        className="rounded-xl !bg-white px-5 !text-[#FF4D4F] !border !border-[#FF4D4F]"
                        onClick={handleDeleteEbookFile}
                      />
                    </div>
                  )}
                </div>
              )}
              <label htmlFor="">
                <div className="mt-6 flex">
                  <FormControl name="isPopular">
                    <Checkbox
                      label="Đánh dấu sách nên đọc"
                      labelClassName="text-sm text-[#83898C]"
                    />
                  </FormControl>
                </div>
              </label>
              <TryListeningCheckbox name="isTryListening" />
              <label htmlFor="">
                <div className="mt-3 flex">
                  <Checkbox
                    label="Sắp ra mắt"
                    labelClassName="text-sm text-[#83898C]"
                    onChange={handleComingSoonChange}
                    checked={isComingSoon}
                  />
                </div>
              </label>
              {isComingSoon && (
                <FormControl name="timePublish">
                  <Input className="mt-3" type="datetime-local" />
                </FormControl>
              )}
              <h2 className="mt-6 mb-4 text-[#2C2D33] font-semibold text-[18px]">
                Phần đầu hoặc Chương
              </h2>
              <FormControl name="elements">
                <Chapter
                  arrayName="elements"
                  endpointConversions={endpointConversions}
                  modelTrains={modelTrains}
                  isUpdate
                />
              </FormControl>
            </div>
            <div className="flex-[4] mt-6 rounded-xl bg-white py-6 px-5">
              <label htmlFor="">
                <div className="mt-6 flex">
                  <Checkbox
                    label="Tạo bìa bằng AI"
                    checked={isGenerateThumbnailsByAI}
                    onChange={handleGenerateThumbnailsByAIChange}
                  />
                </div>
              </label>
              <div className="mb-6 mt-2">
                {!!currentThumbnails.length && (
                  <h2 className="mb-3 text-[#2C2D33] font-medium">
                    Hình ảnh hiện tại
                  </h2>
                )}
                {!!currentThumbnails.length &&
                  currentThumbnails.map((item, index) => (
                    <div
                      key={`${item}-${index}`}
                      className="bg-[#F4F5FA] rounded-lg flex flex-col items-center justify-center relative"
                    >
                      <button
                        type="button"
                        onClick={() => handleRemoveCurrentThumbnail(index)}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white text-sm font-bold z-10 transition-colors"
                        title="Xóa ảnh"
                      >
                        ×
                      </button>
                      <img className="mt-1" alt="" src={s3Url + item} />
                    </div>
                  ))}
              </div>
              <h2 className="mb-4 text-[#2C2D33] font-medium">
                Cập nhật hình ảnh mới
              </h2>
              <div
                id="thumbnail-upload"
                className="bg-[#F4F5FA] h-[436px] rounded-lg flex flex-col items-center justify-center cursor-pointer relative"
                onClick={onAddThumbnailFileClick}
              >
                {thumbnail && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveMainThumbnail();
                    }}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white text-sm font-bold z-10 transition-colors"
                    title="Xóa ảnh"
                  >
                    ×
                  </button>
                )}
                {!thumbnail && (
                  <>
                    <div>
                      <img src={Images.UploadIcon.default} alt="" />
                    </div>
                    <div className="flex gap-3 items-center mt-4">
                      <div>
                        <img src={Images.UploadCloudIcon.default} alt="" />
                      </div>
                      <div className="primary-100">Tải lên ảnh bìa</div>
                    </div>
                    <div className="black-30 text-sm mt-3">
                      Tải lên ảnh bìa cho sản phẩm của bạn, kích thước chấp nhận
                      1080x1920px, định dạng JPG hoặc PNG
                    </div>
                  </>
                )}
                {thumbnail && <img ref={previewThumbnailRef} alt="" />}
              </div>
              <input
                ref={inputThumbnailRef}
                type="file"
                name="thumbnailFile"
                hidden
                onChange={handleThumbnailFileChange}
              />
              {errorMessage.thumbnail && (
                <div className="flex error-color text-xs">
                  {errorMessage.thumbnail}
                </div>
              )}
              <h2 className="mt-6 text-[#2C2D33] font-medium">
                Hình ảnh bổ sung
              </h2>
              {!!additionalThumbnails.length &&
                additionalThumbnails.map((item, index) => (
                  <div
                    key={`${item}-${index}`}
                    className="h-[147px] flex justify-center border border-dashed border-[#A6A8B1] rounded-lg mt-4 relative"
                  >
                    <button
                      type="button"
                      onClick={() => handleRemoveAdditionalThumbnail(index)}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white text-sm font-bold z-10 transition-colors"
                      title="Xóa ảnh"
                    >
                      ×
                    </button>
                    <img className="h-full" alt="" src={s3Url + item} />
                  </div>
                ))}
              <div
                className="bg-[#F4F5FA] h-[147px] rounded-lg mt-3 flex flex-col items-center justify-center cursor-pointer"
                onClick={onAddAdditionalThumbnailFilesClick}
              >
                <div>
                  <img src={Images.UploadIcon.default} alt="" />
                </div>
                <div className="flex gap-3 items-center mt-4">
                  <div>
                    <img src={Images.UploadCloudIcon.default} alt="" />
                  </div>
                  <div className="primary-100">Tải lên ảnh bìa</div>
                </div>
              </div>
              <input
                ref={inputAdditionalThumbnailRef}
                type="file"
                name="additionalThumbnailFiles"
                hidden
                multiple
                onChange={handleAdditionalThumbnailFilesChange}
              />
              {errorMessage.additionalThumbnail && (
                <div className="flex error-color text-xs">
                  {errorMessage.additionalThumbnail}
                </div>
              )}
              <div className="h-[147px] border border-dashed border-[#A6A8B1] rounded-lg mt-4"></div>
            </div>
          </div>
          <div className="mt-7">
            <label htmlFor="">
              <div className="mt-6 flex">
                <FormControl name="isDraft">
                  <Checkbox
                    label="Lưu bản nháp"
                    labelClassName="text-sm text-[#696c6e]"
                  />
                </FormControl>
              </div>
            </label>
          </div>
          <div className="w-full mt-3 flex">
            <Button
              type="submit"
              label="Lưu"
              width={164}
              containerClassName="mr-2"
              className=" h-[52px] text-[#5570F1] rounded-lg border border-solid font-semibold border-[#5570F1]"
              theme="primary"
            />
            <Button
              type="button"
              label="Hủy"
              width={164}
              containerClassName="ml-2"
              className="bg-transparent h-[52px] text-[#5570F1] rounded-lg border font-semibold border-solid border-[#5570F1]"
              theme="secondary"
              onClick={handleCancelClick}
            />
          </div>
        </Form>
      </Formik>
      <div id="audiobook-root">
        <Audiobook />
      </div>
    </div>
  );
}

export default UpdateBookPage;
